import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin } from 'antd';
import styled from 'styled-components';
import {
  allDimensionsListSelector,
  dimensionsListSelectorKeyValue,
} from '@store/common/dimensionList/selectors';
import { needCheckDimensionSelector } from '@store/tempDashboard/selectors';
import { isAppModule, CURRENT_PRODUCT_MODE } from '@utils/constants';
import { getValidFilterDimensionMeta } from '@utils';
import { ErrorBoundary } from '@components/common';
import { getDataSource, convertCustomTagToDimension } from '../components/utils';
import { useFilterMeta } from './hooks';
import RuleAtom from '../components/Dialog/SimpleFilter/RuleAtom';
import * as Types from '../types';
import * as Store from '../components/Dialog/store';

export interface AdvancedFilterBuilderProps<T extends Types.FilterRule = Types.FilterRule> {
  value: T[][];
  onRuleRemove: (rule: T) => void;
  onRuleUpdate: (rule: T, op: string) => void;
}

const filterDataSource = getDataSource();
const filterMetaEnabled = ['EI', 'AI', 'APP'].includes(CURRENT_PRODUCT_MODE);

const StyledContainer = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: 6px;
  border-radius: 4px;
`;

export const AdvancedFilterBuilder = (props: AdvancedFilterBuilderProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const previousAtomCountRef = React.useRef(0);
  const dispatch = useDispatch();
  const allDimensions = useSelector<unknown, any>(allDimensionsListSelector);
  const needCheckDimension = useSelector(needCheckDimensionSelector);
  const dimensionLookup = useSelector<unknown, Record<string, string>>(
    dimensionsListSelectorKeyValue,
  );
  const filterMeta = useFilterMeta(filterDataSource, {
    enabled: filterMetaEnabled,
  });
  const filterMetadata = React.useMemo(() => {
    if (!filterMeta.data || !needCheckDimension) return filterMeta.data;

    const { dimensions } = filterMeta.data;
    const validDimensions = getValidFilterDimensionMeta(dimensions, allDimensions);

    return {
      ...filterMeta.data,
      dimensions: validDimensions,
    };
  }, [filterMeta.data, needCheckDimension, allDimensions]);
  const filterDimensionSet = React.useMemo(
    () =>
      !filterMeta.data
        ? new Set<string>()
        : new Set(
            (
              filterMeta.data.dimensions
                .map((it) => it.fields)
                .flat(Infinity) as Types.DimensionFieldMeta[]
            ).map((it) => it.value),
          ),
    [filterMeta.data],
  );
  const value = React.useMemo(
    () => ({ rule: convertCustomTagToDimension(filterDimensionSet, props.value) }),
    [filterDimensionSet, props.value],
  );

  React.useEffect(() => {
    const currentAtomCount = value.rule.flat(Infinity).length;
    if (currentAtomCount > previousAtomCountRef.current) {
      requestAnimationFrame(() => {
        const parentContainer = containerRef.current?.closest('.ant-drawer-body');

        parentContainer?.scrollTo({
          top: parentContainer.children?.[0]?.clientHeight ?? 9999999,
          behavior: 'smooth',
        });
      });
    }
    previousAtomCountRef.current = currentAtomCount;
  }, [value]);

  if (filterMetaEnabled && (filterMeta.isLoading || filterMeta.isError)) {
    return <Spin style={{ transform: 'translateY(100%)', display: 'block' }} />;
  }

  return (
    <ErrorBoundary>
      <Store.FilterDialogProvider meta={filterMetadata} value={value}>
        <StyledContainer ref={containerRef}>
          {value.rule.map((rules, andIx) => (
            <React.Fragment key={andIx}>
              {rules.map((it, ix) => (
                <RuleAtom
                  key={ix}
                  forceActive
                  dimensionReadonly
                  valueReadonly
                  op={it.op}
                  field={it.field}
                  label={dimensionLookup[it.field]}
                  tagKey={it.key}
                  values={[it]}
                  path={[andIx, ix] as const}
                  onChange={(val) => dispatch(props.onRuleUpdate(it, val.op))}
                  onRemove={() => dispatch(props.onRuleRemove(it))}
                />
              ))}
            </React.Fragment>
          ))}
        </StyledContainer>
      </Store.FilterDialogProvider>
    </ErrorBoundary>
  );
};
